import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
} from "@mui/material";

const NBARegularSeason = () => {
  const [selectedWeek, setSelectedWeek] = useState(1);
  const [nbaTeamSchedule, setNbaTeamSchedule] = useState({});
  const [allWeeks, setAllWeeks] = useState([]);

  // Structured data (WebApplication + FAQPage + Breadcrumb) is now baked into
  // the prerendered HTML and kept in sync by the global <StructuredData />
  // component from a single source (src/config/structured-data.js).

  // ── Load static data ───────────────────────────────────────────
  useEffect(() => {
    const loadScheduleData = async () => {
      try {
        const [scheduleRes, weeksRes] = await Promise.all([
          fetch("/data/schedule.json"),
          fetch("/data/weeks.json"),
        ]);
        const schedule = await scheduleRes.json();
        const weeksData = await weeksRes.json();

        setNbaTeamSchedule(schedule);
        
        // Generate all weeks from weeks data
        if (weeksData?.weeks) {
          const weeks = Object.entries(weeksData.weeks).map(([weekNum, weekData]) => ({
            number: parseInt(weekNum),
            ...weekData,
          })).sort((a, b) => a.number - b.number);
          setAllWeeks(weeks);
          
          // Set current week based on today's date (in US Eastern time)
          const today = new Date();
          const currentWeek = weeks.find(w => {
            const start = parseEasternDate(w.start);
            const end = parseEasternDate(w.end);
            start.setHours(0, 0, 0, 0);
            end.setHours(23, 59, 59, 999);
            return today >= start && today <= end;
          });
          if (currentWeek) {
            setSelectedWeek(currentWeek.number);
          }
        }
      } catch (e) {
        console.error("Error loading schedule data:", e);
      }
    };
    loadScheduleData();
  }, []);

  // ── Helper: Parse date in US Eastern Time ────────────────────────
  const parseEasternDate = (dateStr) => {
    // Parse date string and treat as US Eastern time
    const date = new Date(dateStr + 'T00:00:00-05:00'); // EST offset
    return date;
  };

  // ── Current week data ─────────────────────────────────────────────
  const currentWeekData = useMemo(() => {
    const week = allWeeks.find(w => w.number === selectedWeek);
    if (!week || !nbaTeamSchedule) return null;

    const teams = {};
    // Parse week boundaries in Eastern time
    const weekStart = parseEasternDate(week.start);
    const weekEnd = parseEasternDate(week.end);
    
    // Set to start of Monday and end of Sunday (inclusive)
    weekStart.setHours(0, 0, 0, 0);
    weekEnd.setHours(23, 59, 59, 999);

    Object.entries(nbaTeamSchedule).forEach(([dateStr, teamList]) => {
      const gameDate = parseEasternDate(dateStr);
      
      if (gameDate >= weekStart && gameDate <= weekEnd) {
        teamList.forEach((abbr) => {
          if (!teams[abbr]) teams[abbr] = { team: abbr, games: 0 };
          teams[abbr].games++;
        });
      }
    });

    return {
      week,
      teams: Object.values(teams).sort((a, b) => b.games - a.games),
    };
  }, [selectedWeek, allWeeks, nbaTeamSchedule]);

  const handleWeekSelect = (e) => setSelectedWeek(+e.target.value);

  // Determine if selected week is current week
  const isCurrentWeek = useMemo(() => {
    if (!currentWeekData) return false;
    const today = new Date();
    const weekStart = parseEasternDate(currentWeekData.week.start);
    const weekEnd = parseEasternDate(currentWeekData.week.end);
    weekStart.setHours(0, 0, 0, 0);
    weekEnd.setHours(23, 59, 59, 999);
    return today >= weekStart && today <= weekEnd;
  }, [currentWeekData]);

  // ── Loading guard ─────────────────────────────────────────────
  if (!nbaTeamSchedule || !allWeeks.length || !currentWeekData) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography>Loading schedule data…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,
            color: "primary.main",
            mb: 1,
            textAlign: { xs: "center", md: "left" },
          }}
        >
          NBA Regular Season Schedule - Fantasy Basketball
        </Typography>
        
      </Box>

      {/* Controls */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
          alignItems: "center",
          justifyContent: { xs: "center", md: "flex-start" },
        }}
      >
        <FormControl sx={{ minWidth: 210 }}>
          <InputLabel>Week</InputLabel>
          <Select
            value={selectedWeek}
            label="Week"
            onChange={handleWeekSelect}
          >
            {allWeeks.map((w) => (
              <MenuItem key={w.number} value={w.number}>
                W{w.number} ({w.label})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Chip
          label={`${currentWeekData.week.label}${isCurrentWeek ? ' (Current Week)' : ''}`}
          color={isCurrentWeek ? "primary" : "default"}
          variant={isCurrentWeek ? "filled" : "outlined"}
          sx={{ fontWeight: isCurrentWeek ? 700 : 400 }}
        />
      </Box>

      {/* NBA Teams Table */}
      <TableContainer component={Paper} elevation={3}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "grey.100" }}>
              <TableCell sx={{ fontWeight: 700 }}>Team</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>
                Games in Week {selectedWeek}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currentWeekData.teams.map((t) => (
              <TableRow key={t.team} hover>
                <TableCell>
                  <Chip label={t.team} size="small" />
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: 600,
                    color: t.games >= 4 ? "success.main" : t.games <= 2 ? "error.main" : "text.primary",
                  }}
                >
                  {t.games}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Summary Stats */}
      <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
        <Chip
          label={`Total Teams: ${currentWeekData.teams.length}`}
          variant="outlined"
          color="info"
        />
        <Chip
          label={`Total Games: ${currentWeekData.teams.reduce((sum, t) => sum + t.games, 0)}`}
          variant="outlined"
          color="info"
        />
        <Chip
          label={`Avg Games/Team: ${(currentWeekData.teams.reduce((sum, t) => sum + t.games, 0) / currentWeekData.teams.length).toFixed(1)}`}
          variant="outlined"
          color="info"
        />
      </Box>
    </Box>
  );
};

export default NBARegularSeason;

